import React, {useEffect, useRef, useState} from 'react';
import {Animated, Pressable, StyleSheet, View} from 'react-native';
import type {BottomTabBarProps} from '@react-navigation/bottom-tabs';
import {CommonActions} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';

import {Icon} from '@/shared/ui/icons';
import {ListRow, RowGroup} from '@/shared/ui/list-row';
import {Sheet} from '@/shared/ui/sheet';
import {AppText} from '@/shared/ui/text';
import {layout, radii, shadows, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Нижняя панель телефона, своя, а не стандартная из react-navigation.
 *
 * ## Пять вкладок — потолок, и «Ещё» его держит
 *
 * Шесть вкладок в 390pt — это по 62pt на каждую, и подписи «Сотрудники» и
 * «Посещаемость» в них не помещались: «Сотрудни…», «Посещае…». Обрезанная подпись
 * хуже отсутствующей — она ещё и врёт о том, что за ней. Панель поэтому показывает не
 * больше ПЯТИ вкладок; когда разделов больше, последние (Устройства, Настройки —
 * то, куда заходят раз в неделю и раз в месяц) складываются в «Ещё», лист снизу с
 * теми же значками и подписями. Когда открыт спрятанный раздел, подсвечена «Ещё».
 *
 * Подписи короткие и свои — `tabBarLabel` из навигатора («Явка», не
 * «Посещаемость»): панель — это не оглавление, ей достаточно слова, которое узнают.
 *
 * ## Что она делает иначе, чем стандартная
 *
 *   - 56pt цель на вкладку, вся колонка нажимается, `hitSlop` сверх этого.
 *   - Активная вкладка — глиф в залитой брендом плашке, которая вырастает на место:
 *     выбор — это ФОРМА, а не только цвет, и это единственный признак, который
 *     переживает и дальтонизм, и телефон на солнце.
 *   - Панель ограничена шириной содержимого и отцентрована.
 *   - Она плавает: волосяная линия сверху и тень вверх.
 *
 * Всё, что она рисует, берётся из опций навигатора (`tabBarIcon`, `tabBarLabel`,
 * `title`), так что набор вкладок определён в одном месте — app/navigation.tsx.
 */
const TAB_HEIGHT = 56;
/** Больше вкладок панель не показывает: остальные складываются в «Ещё». */
const MAX_TABS = 5;
const LOZENGE = 56;

const styles = StyleSheet.create({
  bar: {borderTopWidth: StyleSheet.hairlineWidth},
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
    width: '100%',
    maxWidth: layout.maxContentWidth,
    alignSelf: 'center',
    paddingHorizontal: spacing(2),
    paddingTop: spacing(2),
  },
  tab: {
    flex: 1,
    minWidth: 0,
    minHeight: TAB_HEIGHT,
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 3,
    paddingVertical: spacing(1),
    paddingHorizontal: 2,
  },
  lozenge: {
    width: LOZENGE,
    height: 28,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fill: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: radii.md,
  },
  label: {textAlign: 'center'},
});

/** Одна вкладка: плашка + подпись, заливка выезжает по фокусу. */
function Tab({
  focused,
  label,
  icon,
  tint,
  onPress,
  onLongPress,
  testID,
  accessibilityLabel,
}: {
  focused: boolean;
  label: string;
  icon: React.ReactNode;
  tint: string;
  onPress: () => void;
  onLongPress?: () => void;
  testID?: string;
  accessibilityLabel?: string;
}) {
  const p = useTheme();
  // Только opacity и scale: обе идут через нативный драйвер, панель не роняет кадр
  // на медленных устройствах, на которых это приложение тоже должно работать.
  const anim = useRef(new Animated.Value(focused ? 1 : 0)).current;

  useEffect(() => {
    Animated.spring(anim, {
      toValue: focused ? 1 : 0,
      useNativeDriver: true,
      speed: 20,
      bounciness: 6,
    }).start();
  }, [focused, anim]);

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityState={{selected: focused}}
      accessibilityLabel={accessibilityLabel ?? label}
      onPress={onPress}
      onLongPress={onLongPress}
      hitSlop={{top: 6, bottom: 6, left: 4, right: 4}}
      android_ripple={{color: tint, borderless: true, radius: 44}}
      style={({pressed}) => [styles.tab, pressed && !focused && {opacity: 0.6}]}>
      <View style={styles.lozenge}>
        <Animated.View
          style={[
            styles.fill,
            {
              backgroundColor: tint,
              opacity: anim,
              transform: [{scaleX: anim.interpolate({inputRange: [0, 1], outputRange: [0.6, 1]})}],
            },
          ]}
        />
        {icon}
      </View>
      {/* Подпись НЕ берёт краску плашки — она стоит на раме, а не на заливке. */}
      <AppText
        variant="micro"
        color={focused ? p.chromeForeground : p.chromeMuted}
        numberOfLines={1}
        style={styles.label}>
        {label}
      </AppText>
    </Pressable>
  );
}

export function AppTabBar({
  state,
  descriptors,
  navigation,
  insets,
  pinned = [],
}: BottomTabBarProps & {
  /**
   * Разделы, которые не уходят в «Ещё», сколько бы вкладок ни было. Место им
   * освобождают последние из незакреплённых; порядок на панели остаётся порядком
   * навигатора.
   */
  pinned?: readonly string[];
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const [moreOpen, setMoreOpen] = useState(false);

  // Разделы сверх потолка уходят в «Ещё». Порядок сохраняется: в лист попадают
  // последние — те, что и на панели стояли бы последними, — кроме закреплённых.
  const overflow = state.routes.length > MAX_TABS;
  const room = MAX_TABS - 1;
  const must = state.routes.filter(r => pinned.includes(r.name)).slice(0, room);
  const rest = state.routes.filter(r => !must.includes(r));
  const shown = new Set(overflow ? [...must, ...rest.slice(0, room - must.length)] : state.routes);
  const visible = state.routes.filter(r => shown.has(r));
  const hidden = state.routes.filter(r => !shown.has(r));
  const hiddenFocused = hidden.some(r => state.routes.indexOf(r) === state.index);

  const open = (route: (typeof state.routes)[number]) => {
    const focused = state.routes.indexOf(route) === state.index;
    const event = navigation.emit({
      type: 'tabPress',
      target: route.key,
      canPreventDefault: true,
    });
    // Тот же dispatch, что и у стандартной панели: `target: state.key` нацеливает
    // действие на ЭТОТ навигатор, и повторное нажатие на активную вкладку
    // сбрасывает её стек к корню.
    if (!focused && !event.defaultPrevented) {
      navigation.dispatch({
        ...CommonActions.navigate(route.name, route.params),
        target: state.key,
      });
    }
  };

  const labelOf = (route: (typeof state.routes)[number]) => {
    const {options} = descriptors[route.key];
    const short = options.tabBarLabel;
    return typeof short === 'string' ? short : options.title ?? route.name;
  };

  return (
    <View
      style={[
        styles.bar,
        shadows.pop,
        {
          backgroundColor: p.chrome,
          borderTopColor: p.chromeBorder,
          // Отступ под индикатор «домой» — padding, не margin: поверхность панели
          // доходит до края экрана, только её содержимое останавливается раньше.
          paddingBottom: insets.bottom + spacing(2),
        },
      ]}>
      <View style={styles.row}>
        {visible.map(route => {
          const index = state.routes.indexOf(route);
          const {options} = descriptors[route.key];
          const focused = state.index === index;
          // Глиф рисуется НА заливке бренда, когда та выехала, — берёт её краску;
          // без фокуса — приглушённую краску рамы.
          const color = focused ? p.primaryForeground : p.chromeMuted;

          return (
            <Tab
              key={route.key}
              focused={focused}
              label={labelOf(route)}
              tint={p.primary}
              icon={options.tabBarIcon?.({focused, color, size: 20})}
              onPress={() => open(route)}
              onLongPress={() => navigation.emit({type: 'tabLongPress', target: route.key})}
              testID={options.tabBarButtonTestID ?? `tab-${route.name}`}
              accessibilityLabel={options.tabBarAccessibilityLabel}
            />
          );
        })}

        {overflow ? (
          <Tab
            focused={hiddenFocused}
            label={t('nav.more')}
            tint={p.primary}
            icon={
              <Icon
                name="menu"
                size={20}
                color={hiddenFocused ? p.primaryForeground : p.chromeMuted}
              />
            }
            onPress={() => setMoreOpen(true)}
            testID="tab-More"
          />
        ) : null}
      </View>

      {overflow ? (
        <Sheet
          testID="more-sheet"
          visible={moreOpen}
          onClose={() => setMoreOpen(false)}
          title={t('shell.moreTitle')}>
          <RowGroup>
            {hidden.map(route => {
              const {options} = descriptors[route.key];
              const focused = state.routes.indexOf(route) === state.index;
              return (
                <ListRow
                  key={route.key}
                  testID={`tab-${route.name}`}
                  leading={options.tabBarIcon?.({
                    focused,
                    color: focused ? p.primary : p.mutedForeground,
                    size: 20,
                  })}
                  title={options.title ?? route.name}
                  onPress={() => {
                    setMoreOpen(false);
                    open(route);
                  }}
                />
              );
            })}
          </RowGroup>
        </Sheet>
      ) : null}
    </View>
  );
}
